import { useState } from 'react'
import { Plus, FileText } from 'lucide-react'
import { useNavigate } from '@tanstack/react-router'
import {
  Button,
  DataTable,
  EmptyState,
  MetricCard,
  MetricStrip,
  SearchInput,
  Select,
  Toolbar,
  ToolbarLeft,
  ToolbarRight,
} from '@/components/ui'
import type { ColumnDef } from '@tanstack/react-table'
import { useReports, useReportStats } from '../api/queries'
import { DisciplinaryStatusChip } from '../components/DisciplinaryStatusChip'
import type { DisciplinaryReport, ViolationType } from '../api/types'
import { VIOLATION_TYPE_LABELS, DISCIPLINE_ACTION_LABELS } from '../api/types'

function fmtDate(d: string | null): string {
  if (!d) return '—'
  return new Date(d).toLocaleDateString()
}

function ViolationChips({ types }: { types: ViolationType[] }) {
  if (!types || types.length === 0) return <span className="text-secondary">—</span>
  return (
    <div className="flex flex-wrap gap-1">
      {types.map((t) => (
        <span
          key={t}
          className="text-xs rounded px-1.5 py-0.5 bg-surface-raised border border-border-default"
        >
          {VIOLATION_TYPE_LABELS[t] ?? t}
        </span>
      ))}
    </div>
  )
}

const COLUMNS: ColumnDef<DisciplinaryReport>[] = [
  {
    accessorKey: 'report_number',
    header: 'Report #',
    size: 130,
    cell: ({ getValue }) => (
      <span className="text-role-data-primary">{getValue() as string}</span>
    ),
  },
  {
    accessorKey: 'date_of_incident',
    header: 'Incident Date',
    size: 120,
    cell: ({ getValue }) => fmtDate(getValue() as string | null),
  },
  {
    accessorKey: 'employee_name',
    header: 'Employee',
    size: 160,
    cell: ({ getValue }) =>
      (getValue() as string) || <span className="text-secondary">—</span>,
  },
  {
    accessorKey: 'department',
    header: 'Department',
    size: 140,
    cell: ({ getValue }) =>
      (getValue() as string) || <span className="text-secondary">—</span>,
  },
  {
    accessorKey: 'violation_types',
    header: 'Violation(s)',
    size: 220,
    cell: ({ getValue }) => <ViolationChips types={getValue() as ViolationType[]} />,
  },
  {
    accessorKey: 'discipline_action',
    header: 'Action',
    size: 180,
    cell: ({ getValue }) => {
      const v = getValue() as string | null
      if (!v) return <span className="text-secondary">—</span>
      return DISCIPLINE_ACTION_LABELS[v as keyof typeof DISCIPLINE_ACTION_LABELS] ?? v
    },
  },
  {
    accessorKey: 'status',
    header: 'Status',
    size: 120,
    cell: ({ getValue }) => (
      <DisciplinaryStatusChip status={getValue() as DisciplinaryReport['status']} />
    ),
  },
  {
    id: 'pdf',
    header: 'PDF',
    size: 60,
    cell: ({ row }) =>
      row.original.pdf_file_id ? (
        <a
          href={`/api/exports/${row.original.pdf_file_id}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="text-brand hover:underline"
          title="Download PDF"
        >
          <FileText size={16} />
        </a>
      ) : (
        <span className="text-secondary">—</span>
      ),
  },
]

export function DisciplinaryReportsPage() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('')

  const { data, isLoading, isError, error, refetch } = useReports({
    search: search || undefined,
    status: statusFilter || undefined,
  })
  const { data: stats } = useReportStats()

  const rows = data?.items ?? []

  return (
    <div className="flex flex-col h-full">
      {stats && (
        <MetricStrip>
          <MetricCard label="Total" value={stats.total} />
          <MetricCard label="Draft" value={stats.draft} />
          <MetricCard label="Issued" value={stats.issued} />
          <MetricCard label="Acknowledged" value={stats.acknowledged} />
        </MetricStrip>
      )}

      {/* Search bar — own full-width row */}
      <Toolbar>
        <ToolbarLeft>
          <SearchInput
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search report #, employee, department…"
            containerClassName="flex-1 max-w-none"
          />
        </ToolbarLeft>
      </Toolbar>

      {/* Controls */}
      <Toolbar>
        <ToolbarLeft>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All statuses</option>
            <option value="draft">Draft</option>
            <option value="issued">Issued</option>
            <option value="acknowledged">Acknowledged</option>
          </Select>
        </ToolbarLeft>
        <ToolbarRight>
          <Button
            variant="primary"
            size="sm"
            onClick={() => navigate({ to: '/disciplinary-reports/new' })}
          >
            <Plus size={14} className="mr-1" />
            New Report
          </Button>
        </ToolbarRight>
      </Toolbar>

      <div className="flex-1 overflow-hidden">
        {rows.length === 0 && !isLoading && !isError ? (
          <EmptyState
            title="No disciplinary reports"
            description="Create a new report using the button above."
          />
        ) : (
          <DataTable
            error={isError ? error : undefined}
            onRetry={() => void refetch()}
            errorSubject="disciplinary reports"
            columns={COLUMNS}
            data={rows}
            loading={isLoading}
            onRowClick={(row) =>
              navigate({
                to: '/disciplinary-reports/$reportId',
                params: { reportId: row.id },
              })
            }
          />
        )}
      </div>
    </div>
  )
}
